import { useNavigate } from 'react-router-dom';
import { formatMessage } from '@bookingdei/i18n/interpolate';
import { formatClock, formatDate } from '@bookingdei/i18n/format-date';
import { useLanguage } from '../../shell/language-context';
import { scheduleDayLabel } from './home-schedule';
import { localDay } from './home-model';
import { formatPhone } from '@bookingdei/i18n/phone';
import { phonePlan } from '../../theme/phone-plan';
import type { MerchantBooking } from '../../mock/seeds/schema-types';

export interface FreeGateView {
  /** `bookings_per_month` entitlement (DR-31). */
  cap: number;
  /** `bookings_uncapped_months` and the end of that window (null = no promo). */
  promoMonths: number;
  promoEnds: Date | null;
  usedThisMonth: number;
  /** Rows the server masked this month (API-95 `hidden`). */
  hidden: readonly MerchantBooking[];
}

// Free plan (DR-31, design blurredNotice M:519-536 + freeCard M:754-765). While the
// uncapped promo runs: a card naming its end date and the monthly cap that follows.
// After it: the "Free plan limit" notice with the over-cap rows — masked by the server
// (API-95 sets `hidden` and strips name/phone), drawn blurred with only the time kept —
// then the usage card. Upgrade goes to My Plan. Rendered only for a capped plan.
export function HomeFreeNotice({ gate, now }: { gate: FreeGateView; now: Date }) {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const promo = gate.promoEnds !== null && gate.promoEnds.getTime() > now.getTime();
  if (promo || gate.hidden.length === 0) return null;
  const today = localDay(now);
  return (
    <section className="hm-card hm-free" aria-labelledby="hm-free-kicker">
      <span id="hm-free-kicker" className="hm-free__kicker">
        {t('dc71.free.kicker')}
      </span>
      <p className="hm-free__notice">{formatMessage(lang, 'dc71.free.blurred', { cap: gate.cap, n: gate.hidden.length })}</p>
      <div className="hm-free__rows">
        {gate.hidden.map((b) => {
          const d = b.starts_at ? new Date(b.starts_at) : null;
          const offset = d ? Math.round((new Date(`${localDay(d)}T00:00:00`).getTime() - new Date(`${today}T00:00:00`).getTime()) / 86_400_000) : 0;
          return (
            <div key={b.id} className="hm-free__row">
              <span className="hm-free__name hm-blur" aria-label={t('dc71.free.hidden_row')}>
                {b.customer_name ?? '••••••••'}
              </span>
              <span className="hm-free__phone hm-blur" aria-hidden="true">
                {b.customer_phone ? formatPhone(b.customer_phone, phonePlan) : '•• ••• •••'}
              </span>
              <span className="hm-free__time">
                {d
                  ? `${Math.abs(offset) <= 1 ? scheduleDayLabel(lang, offset, now, t) : formatDate(lang, d, { year: false })} ${formatClock(lang, d.getHours(), d.getMinutes())}`
                  : '—'}
              </span>
            </div>
          );
        })}
      </div>
      <button type="button" className="hm-btn hm-btn--lg" onClick={() => navigate('/plan')}>
        {t('dc71.free.upgrade')}
      </button>
    </section>
  );
}

export function HomeFreeCard({ gate, now }: { gate: FreeGateView; now: Date }) {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const promo = gate.promoEnds !== null && gate.promoEnds.getTime() > now.getTime();
  const title = promo
    ? formatMessage(lang, 'dc71.free.promo_title', {
        months: gate.promoMonths,
        date: formatDate(lang, gate.promoEnds!, { year: false, month: 'long' }),
      })
    : formatMessage(lang, 'dc71.free.cap_title', { used: gate.usedThisMonth, cap: gate.cap, hidden: gate.hidden.length });
  return (
    <section className="hm-card" aria-labelledby="hm-free-title">
      <div className="hm-card__head">
        <h3 id="hm-free-title" className="hm-card__title">
          {title}
        </h3>
      </div>
      <div className="hm-freecard__body">
        <p className="hm-freecard__help">{promo ? formatMessage(lang, 'dc71.free.promo_help', { cap: gate.cap }) : t('dc71.free.cap_help')}</p>
        <button type="button" className="hm-btn" onClick={() => navigate('/plan')}>
          {t('dc71.free.upgrade')}
        </button>
      </div>
    </section>
  );
}
